import {
  AtSign,
  Globe,
  Mail,
  MessageCircle,
  MessageSquare,
  Phone,
  Star,
  StickyNote,
} from "lucide-react";
import * as React from "react";
import { cn } from "../lib/cn";
import { formatMessageAge, shortName } from "../lib/format";
import { channelMeta, type CommunicationChannel } from "../lib/status";
import { Avatar } from "./avatar";

const CHANNEL_ICONS: Record<CommunicationChannel, React.ComponentType<{ className?: string }>> = {
  instagram: AtSign,
  whatsapp: MessageCircle,
  sms: MessageSquare,
  email: Mail,
  web_chat: Globe,
  phone: Phone,
  internal_note: StickyNote,
  google_review: Star,
};

export function ChannelIcon({
  channel,
  className,
}: {
  channel: CommunicationChannel;
  className?: string;
}) {
  const Icon = CHANNEL_ICONS[channel];
  return (
    <>
      <Icon className={cn("size-4", className)} aria-hidden />
      <span className="sr-only">{channelMeta[channel].label}</span>
    </>
  );
}

export type ConversationRowProps = {
  client?: { firstName?: string | null; lastName?: string | null; avatarUrl?: string | null } | null;
  /** Nom d'affichage du canal quand la cliente n'est pas encore rapprochée. */
  fallbackName?: string | null;
  channel: CommunicationChannel;
  preview?: string | null;
  lastMessageAt: Date;
  unreadCount: number;
  direction?: "inbound" | "outbound" | null;
  assigneeName?: string | null;
  /** Fenêtre de réponse Insta/WhatsApp bientôt close — urgence réelle. */
  respondByAt?: Date | null;
  selected?: boolean;
  /** Pose sur le bouton : sert a faire defiler jusqu'a la ligne au clavier. */
  id?: string;
  onClick?: () => void;
  now?: Date;
  className?: string;
};

/**
 * Ligne de conversation du hub.
 *
 * L'écran le plus consulté de l'équipe : tout se joue au balayage vertical.
 * Trois signaux seulement, et ils ne se cumulent jamais visuellement —
 * non lu (gras + pastille), fenêtre qui se ferme (ambre), assignée (discret).
 *
 * L'icône de canal est un rappel, pas une catégorie : la conversation
 * appartient à la cliente, pas au canal.
 */
export function ConversationRow({
  client,
  fallbackName,
  channel,
  preview,
  lastMessageAt,
  unreadCount,
  direction,
  assigneeName,
  respondByAt,
  selected,
  id,
  onClick,
  now = new Date(),
  className,
}: ConversationRowProps) {
  const unread = unreadCount > 0;
  const name = client
    ? shortName(client.firstName, client.lastName)
    : (fallbackName ?? "Contact inconnu");

  // On n'alerte que dans les 4 dernières heures : au-delà, ce n'est pas urgent.
  const closingSoon =
    respondByAt !== null &&
    respondByAt !== undefined &&
    respondByAt.getTime() - now.getTime() < 4 * 3_600_000 &&
    respondByAt.getTime() > now.getTime();

  return (
    <button
      type="button"
      id={id}
      onClick={onClick}
      aria-current={selected || undefined}
      className={cn(
        "flex w-full items-start gap-3 rounded-lg px-3 py-3 text-left",
        "transition-colors duration-[120ms]",
        selected ? "bg-blush-50" : "hover:bg-creme-100",
        className,
      )}
    >
      <div className="relative shrink-0">
        <Avatar
          src={client?.avatarUrl}
          firstName={client?.firstName ?? fallbackName}
          lastName={client?.lastName}
        />
        <span
          className="absolute -bottom-0.5 -right-0.5 flex size-5 items-center justify-center rounded-full bg-white text-encre-500 shadow-soft"
          title={channelMeta[channel].label}
        >
          <ChannelIcon channel={channel} className="size-3" />
        </span>
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span
            className={cn(
              "truncate text-[15px] text-encre-900",
              unread ? "font-semibold" : "font-medium",
            )}
          >
            {name}
          </span>
          <time
            dateTime={lastMessageAt.toISOString()}
            className={cn(
              "shrink-0 text-xs",
              unread ? "font-medium text-blush-600" : "text-encre-400",
            )}
          >
            {formatMessageAge(lastMessageAt, now)}
          </time>
        </div>

        <div className="mt-0.5 flex items-center gap-2">
          <p
            className={cn(
              "min-w-0 flex-1 truncate text-sm",
              unread ? "text-encre-700" : "text-encre-400",
            )}
          >
            {direction === "outbound" && <span className="text-encre-400">Vous : </span>}
            {preview ?? "…"}
          </p>
          {unread && (
            <span className="flex min-w-5 shrink-0 items-center justify-center rounded-full bg-blush-500 px-1.5 text-xs font-medium text-white">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </div>

        {(closingSoon || assigneeName) && (
          <div className="mt-1 flex items-center gap-2 text-xs">
            {closingSoon && (
              <span className="font-medium text-ambre-700">Fenêtre de réponse bientôt close</span>
            )}
            {assigneeName && <span className="text-encre-400">· {assigneeName}</span>}
          </div>
        )}
      </div>
    </button>
  );
}
